export type ParsedVersion = {
  major: number
  minor: number
  patch: number
}

/**
 * Parses a semantic version string
 * @example
 * parseVersion("2.574.0") => { major: 2, minor: 574, patch: 0 }
 */
export const parseVersion = (version: string): ParsedVersion => {
  const match = version.match(/^(\d+)\.(\d+)\.(\d+)$/)

  if (!match) {
    throw new Error(`Invalid version format: ${version}`)
  }

  const [, major, minor, patch] = match

  return {
    major: Number(major),
    minor: Number(minor),
    patch: Number(patch),
  }
}

/**
 * Compares two parsed versions
 * @returns positive if latest > current, 0 if equal, negative if latest < current
 * @example
 * compareVersions(parseVersion("2.575.0"), parseVersion("2.574.0")) => 1
 */
export const compareVersions = (latest: ParsedVersion, current: ParsedVersion): number => {
  if (latest.major !== current.major) {
    return latest.major - current.major
  }
  if (latest.minor !== current.minor) {
    return latest.minor - current.minor
  }
  return latest.patch - current.patch
}

/**
 * Determines if a new version is newer than the old version
 * @param newVersion - Version to check
 * @param oldVersion - Current version
 * @returns true if newVersion > oldVersion
 */
export const isVersionNewer = (newVersion: string, oldVersion: string) => {
  try {
    const newVer = parseVersion(newVersion)
    const oldVer = parseVersion(oldVersion)

    return compareVersions(newVer, oldVer) > 0
  } catch (error) {
    console.error('Error parsing versions:', error)
    return false
  }
}
